import { catalogServiceClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type { CatalogPage } from '../types/catalog-enrichment.types';
import type { CatalogEnvelope, CatalogListEnvelope } from '../types/catalog-service.api.types';
import type {
  ScheduleConfigPatch,
  ScheduleDetail,
  ScheduleFeedRow,
  ScheduleFired,
  ScheduleFiringRead,
  ScheduleOverview,
  SchedulePreview,
  SchedulePreviewRequest,
  ScheduleRead,
  ScheduleReplace,
  ScheduleRunConfigPatch,
  SchedulesTree,
  ScheduleTree,
  ScheduleTriggerOptions,
  ScheduleUpsert,
} from '../types/schedules.api.types';
import type { ScheduleFeedFilters, SchedulesListFilters } from '../types/schedules.types';
import { toPage, toSearchParams, unwrap } from '../utils/envelope';

const api = endpoints.catalogService.schedules;

/** Rows pass through unchanged — the wire row is already what the table renders. */
const same = <T>(row: T): T => row;

/** The schedule dashboard: counts, 30-day rollup, next fires, needs-a-person. */
export async function getSchedulesOverview(): Promise<ScheduleOverview> {
  const body = await catalogServiceClient
    .get(api.overview())
    .json<CatalogEnvelope<ScheduleOverview>>();
  return unwrap(body);
}

/** One page of schedules, newest-touched first, with 30-day stats per row. */
export async function listSchedules(
  filters: SchedulesListFilters & { page: number; size: number },
): Promise<CatalogPage<ScheduleRead>> {
  const body = await catalogServiceClient
    .get(api.root(), { searchParams: toSearchParams({ ...filters }) })
    .json<CatalogListEnvelope<ScheduleRead>>();
  return toPage(body, same);
}

/** One schedule with Temporal's live view. */
export async function getSchedule(scheduleId: string): Promise<ScheduleDetail> {
  const body = await catalogServiceClient
    .get(api.byId(scheduleId))
    .json<CatalogEnvelope<ScheduleDetail>>();
  return unwrap(body);
}

/** One schedule's firing history, newest first, skips included with their reason. */
export async function listScheduleFirings(
  scheduleId: string,
  params: { page: number; size: number },
): Promise<CatalogPage<ScheduleFiringRead>> {
  const body = await catalogServiceClient
    .get(api.firings(scheduleId), { searchParams: toSearchParams(params) })
    .json<CatalogListEnvelope<ScheduleFiringRead>>();
  return toPage(body, same);
}

/** Every schedule's firings (tenant-wide feed), newest first by scheduled time. */
export async function listScheduleFeed(
  filters: ScheduleFeedFilters & { page: number; size: number },
): Promise<CatalogPage<ScheduleFeedRow>> {
  const body = await catalogServiceClient
    .get(api.runs(), { searchParams: toSearchParams({ ...filters }) })
    .json<CatalogListEnvelope<ScheduleFeedRow>>();
  return toPage(body, same);
}

/** Dry-run a create: canonical cron, next five fires, name taken — nothing written. */
export async function previewSchedule(json: SchedulePreviewRequest): Promise<SchedulePreview> {
  const body = await catalogServiceClient
    .post(api.preview(), { json })
    .json<CatalogEnvelope<SchedulePreview>>();
  return unwrap(body);
}

/** Create a schedule; its flow is resolved and frozen server-side. */
export async function createSchedule(json: ScheduleUpsert): Promise<ScheduleRead> {
  const body = await catalogServiceClient
    .post(api.root(), { json })
    .json<CatalogEnvelope<ScheduleRead>>();
  return unwrap(body);
}

/**
 * Rename + replace the WHOLE cadence — PUT …/{id}. A cadence key not sent goes
 * back to its default (unlike the PATCH); the trigger can never be touched here.
 */
export async function replaceSchedule(
  scheduleId: string,
  json: ScheduleReplace,
): Promise<ScheduleRead> {
  const body = await catalogServiceClient
    .put(api.byId(scheduleId), { json })
    .json<CatalogEnvelope<ScheduleRead>>();
  return unwrap(body);
}

/** WHEN it fires — cadence only (cron/calendar/interval, timezone, overlap, catch-up). */
export async function patchScheduleCadence(
  scheduleId: string,
  json: ScheduleConfigPatch,
): Promise<ScheduleRead> {
  const body = await catalogServiceClient
    .patch(api.config(scheduleId), { json })
    .json<CatalogEnvelope<ScheduleRead>>();
  return unwrap(body);
}

/** WHAT it fires — filters, maxProducts, batch sizes (re-validated as a whole trigger). */
export async function patchScheduleRunConfig(
  scheduleId: string,
  json: ScheduleRunConfigPatch,
): Promise<ScheduleRead> {
  const body = await catalogServiceClient
    .patch(api.runConfig(scheduleId), { json })
    .json<CatalogEnvelope<ScheduleRead>>();
  return unwrap(body);
}

/** Stop firing until resumed. */
export async function pauseSchedule(scheduleId: string): Promise<ScheduleRead> {
  const body = await catalogServiceClient
    .post(api.pause(scheduleId))
    .json<CatalogEnvelope<ScheduleRead>>();
  return unwrap(body);
}

/** Start firing again. */
export async function resumeSchedule(scheduleId: string): Promise<ScheduleRead> {
  const body = await catalogServiceClient
    .post(api.resume(scheduleId))
    .json<CatalogEnvelope<ScheduleRead>>();
  return unwrap(body);
}

/** Fire once now; a firing that cannot start is recorded as a skip with its reason. */
export async function fireScheduleNow(scheduleId: string): Promise<ScheduleFired> {
  const body = await catalogServiceClient
    .post(api.trigger(scheduleId))
    .json<CatalogEnvelope<ScheduleFired>>();
  return unwrap(body);
}

/** Soft delete: stops firing and leaves the list; its firing history stays readable. */
export async function deleteSchedule(scheduleId: string): Promise<void> {
  await catalogServiceClient.delete(api.byId(scheduleId));
}

/** Connectors, use cases, flows and maxProducts rules the create form may offer. */
export async function getScheduleTriggerOptions(): Promise<ScheduleTriggerOptions> {
  const body = await catalogServiceClient
    .get(endpoints.catalogService.operations.triggerOptions())
    .json<CatalogEnvelope<ScheduleTriggerOptions>>();
  return unwrap(body);
}

/** The 20 most recently touched schedules, each with its newest 20 firings' trees. */
export async function getSchedulesTree(): Promise<SchedulesTree> {
  const body = await catalogServiceClient.get(api.tree()).json<CatalogEnvelope<SchedulesTree>>();
  return unwrap(body);
}

/** One schedule's newest 20 firings, each with its run's nested execution tree. */
export async function getScheduleTree(scheduleId: string): Promise<ScheduleTree> {
  const body = await catalogServiceClient
    .get(api.scheduleTree(scheduleId))
    .json<CatalogEnvelope<ScheduleTree>>();
  return unwrap(body);
}
